import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CalendarDays, Pencil, Play, Plus, Trash2 } from 'lucide-react';

import { listDepartments } from '@/api/employees.api';
import {
  createMeeting,
  createMeetingSeries,
  DEFAULT_L10_AGENDA,
  deactivateMeetingSeries,
  listMeetings,
  listMeetingSeries,
  startMeeting,
  updateMeetingSeries,
  type AgendaSegment,
  type MeetingListItem,
  type MeetingSeries,
  type MeetingStatus,
} from '@/api/eos/meetings.api';
import { Badge, type BadgeProps } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { describeEosError } from '@/lib/eos/errors';
import { hasCapability, isSuperadmin } from '@/lib/capabilities';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';

/**
 * EOS L10 Meetings — Stream B Wave 2 (ADR-023).
 *
 * Upcoming + past occurrences for every member; series management
 * (create/edit/deactivate + "schedule next") only for `run_meetings`
 * holders and superadmins. Client-side gating is VISIBILITY only —
 * the server re-enforces on every request.
 */

const SELECT_CLS =
  'rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20';

const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const MEETING_STATUS_META: Record<MeetingStatus, { label: string; variant: BadgeProps['variant'] }> = {
  scheduled: { label: 'Scheduled', variant: 'neutral' },
  in_progress: { label: 'In progress', variant: 'info' },
  concluded: { label: 'Concluded', variant: 'success' },
  canceled: { label: 'Canceled', variant: 'warning' },
};

function formatMeetingTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * The next local datetime matching the series' dayOfWeek + "HH:MM"
 * startTime, strictly in the future — feeds "Schedule next meeting".
 */
function nextOccurrence(dayOfWeek: number, startTime: string): Date {
  const [hh = '9', mm = '0'] = startTime.split(':');
  const now = new Date();
  const candidate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), Number(hh), Number(mm), 0, 0);
  candidate.setDate(candidate.getDate() + ((dayOfWeek - candidate.getDay() + 7) % 7));
  if (candidate.getTime() <= now.getTime()) candidate.setDate(candidate.getDate() + 7);
  return candidate;
}

export function MeetingsPage() {
  const { state: authState } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  const me = authState.status === 'authenticated' ? authState.me : null;
  const canRunMeetings = me !== null && (isSuperadmin(me) || hasCapability(me, 'run_meetings'));

  const [seriesModal, setSeriesModal] = useState<{ open: boolean; editing: MeetingSeries | null }>({
    open: false,
    editing: null,
  });

  const meetingsQuery = useQuery({ queryKey: ['eos', 'meetings'], queryFn: () => listMeetings() });
  const seriesQuery = useQuery({
    queryKey: ['eos', 'meeting-series'],
    queryFn: listMeetingSeries,
    enabled: canRunMeetings,
  });
  const departmentsQuery = useQuery({
    queryKey: ['departments'],
    queryFn: listDepartments,
    enabled: canRunMeetings,
  });

  const departmentName = useMemo(() => {
    const out = new Map<string, string>();
    for (const d of departmentsQuery.data ?? []) out.set(d.id, d.name);
    return out;
  }, [departmentsQuery.data]);

  const meetings = useMemo(() => meetingsQuery.data ?? [], [meetingsQuery.data]);
  const upcoming = useMemo(
    () =>
      meetings
        .filter((m) => m.status === 'scheduled' || m.status === 'in_progress')
        .sort((a, b) => a.scheduledFor.localeCompare(b.scheduledFor)),
    [meetings],
  );
  // The list endpoint already sorts scheduledFor desc — newest past first.
  const past = useMemo(
    () => meetings.filter((m) => m.status === 'concluded' || m.status === 'canceled'),
    [meetings],
  );

  function invalidateMeetings(): void {
    void queryClient.invalidateQueries({ queryKey: ['eos', 'meetings'] });
  }
  function invalidateSeries(): void {
    void queryClient.invalidateQueries({ queryKey: ['eos', 'meeting-series'] });
  }

  const startMutation = useMutation({
    mutationFn: (meetingId: string) => startMeeting(meetingId),
    onSuccess: (meeting) => {
      invalidateMeetings();
      void navigate(`/eos/meetings/${meeting.id}/run`);
    },
    onError: (err) => {
      toast.show(describeEosError(err, 'Could not start the meeting.'), { variant: 'danger' });
    },
  });

  const scheduleNextMutation = useMutation({
    mutationFn: (series: MeetingSeries) =>
      createMeeting({
        seriesId: series.id,
        scheduledFor: nextOccurrence(series.dayOfWeek, series.startTime).toISOString(),
      }),
    onSuccess: (meeting) => {
      invalidateMeetings();
      toast.show(`Meeting scheduled for ${formatMeetingTime(meeting.scheduledFor)}`, {
        variant: 'success',
      });
    },
    onError: (err) => {
      toast.show(describeEosError(err, 'Could not schedule the meeting.'), { variant: 'danger' });
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (seriesId: string) => deactivateMeetingSeries(seriesId),
    onSuccess: () => {
      invalidateSeries();
      toast.show('Series deactivated', { variant: 'success' });
    },
    onError: (err) => {
      toast.show(describeEosError(err, 'Could not deactivate the series.'), { variant: 'danger' });
    },
  });

  function renderMeeting(m: MeetingListItem) {
    const meta = MEETING_STATUS_META[m.status];
    return (
      <li key={m.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-medium text-kw-shark">{m.seriesName ?? 'L10 meeting'}</p>
          <p className="mt-0.5 text-[12px] text-kw-muted">
            {formatMeetingTime(m.scheduledFor)}
            {m.attendeeCount > 0
              ? ` · ${String(m.attendeeCount)} ${m.attendeeCount === 1 ? 'attendee' : 'attendees'}`
              : ''}
          </p>
        </div>
        <Badge variant={meta.variant}>{meta.label}</Badge>
        {m.status === 'concluded' && m.averageRating !== null ? (
          <Badge variant="info">Rated {m.averageRating.toFixed(1)}/10</Badge>
        ) : null}
        {m.status === 'scheduled' ? (
          <Button
            size="sm"
            isLoading={startMutation.isPending && startMutation.variables === m.id}
            onClick={() => {
              startMutation.mutate(m.id);
            }}
          >
            <Play className="size-3.5" />
            Start L10
          </Button>
        ) : m.status === 'in_progress' ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              void navigate(`/eos/meetings/${m.id}/run`);
            }}
          >
            Join
          </Button>
        ) : m.status === 'concluded' ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              void navigate(`/eos/meetings/${m.id}/run`);
            }}
          >
            View
          </Button>
        ) : null}
      </li>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="typo-h1">L10 Meetings</h1>
          <p className="mt-1 text-[14px] text-kw-muted">
            The weekly Level 10 — same day, same time, same agenda.
          </p>
        </div>
        {canRunMeetings ? (
          <Button
            onClick={() => {
              setSeriesModal({ open: true, editing: null });
            }}
          >
            <Plus className="size-4" />
            New series
          </Button>
        ) : null}
      </div>

      {meetingsQuery.isLoading ? (
        <div className="flex justify-center py-16">
          <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
        </div>
      ) : meetingsQuery.error ? (
        <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
          <div>
            <p className="text-[14px] font-medium text-kw-danger-text">Couldn&apos;t load meetings</p>
            <p className="mt-1 text-[13px] text-kw-danger-text/80">
              {meetingsQuery.error instanceof Error ? meetingsQuery.error.message : 'Unknown error'}
            </p>
          </div>
        </Card>
      ) : meetings.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <CalendarDays className="size-10 text-kw-ghost" />
          <div>
            <p className="text-[15px] font-medium text-kw-shark">No meetings yet</p>
            <p className="mt-1 text-[13px] text-kw-muted">
              {canRunMeetings
                ? 'Create a series below, then schedule its first occurrence.'
                : 'Your team’s L10 meetings will appear here once they are scheduled.'}
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-6">
          <section className="space-y-2">
            <h2 className="text-[13px] font-medium uppercase tracking-wide text-kw-muted">Upcoming</h2>
            <Card className="p-0">
              {upcoming.length === 0 ? (
                <p className="px-4 py-6 text-center text-[13px] text-kw-muted">Nothing on the calendar.</p>
              ) : (
                <ul className="divide-y divide-kw-border">{upcoming.map(renderMeeting)}</ul>
              )}
            </Card>
          </section>

          {past.length > 0 ? (
            <section className="space-y-2">
              <h2 className="text-[13px] font-medium uppercase tracking-wide text-kw-muted">Past</h2>
              <Card className="p-0">
                <ul className="divide-y divide-kw-border">{past.map(renderMeeting)}</ul>
              </Card>
            </section>
          ) : null}
        </div>
      )}

      {canRunMeetings ? (
        <section className="space-y-2">
          <h2 className="text-[13px] font-medium uppercase tracking-wide text-kw-muted">Meeting series</h2>
          <Card className="p-0">
            {seriesQuery.isLoading ? (
              <p className="px-4 py-6 text-center text-[13px] text-kw-muted">Loading series…</p>
            ) : (seriesQuery.data ?? []).length === 0 ? (
              <p className="px-4 py-6 text-center text-[13px] text-kw-muted">
                No series yet — create one to anchor the weekly cadence.
              </p>
            ) : (
              <ul className="divide-y divide-kw-border">
                {(seriesQuery.data ?? []).map((s) => (
                  <li key={s.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-[14px] font-medium text-kw-shark">{s.name}</p>
                      <p className="mt-0.5 text-[12px] text-kw-muted">
                        {DAY_LABELS[s.dayOfWeek] ?? 'Unknown day'}s at {s.startTime} · {s.durationMinutes} min
                        ·{' '}
                        {s.departmentId
                          ? (departmentName.get(s.departmentId) ?? 'Unknown department')
                          : 'Company / leadership'}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="secondary"
                      isLoading={scheduleNextMutation.isPending && scheduleNextMutation.variables.id === s.id}
                      onClick={() => {
                        scheduleNextMutation.mutate(s);
                      }}
                    >
                      <CalendarDays className="size-3.5" />
                      Schedule next
                    </Button>
                    <button
                      type="button"
                      aria-label={`Edit ${s.name}`}
                      onClick={() => {
                        setSeriesModal({ open: true, editing: s });
                      }}
                      className="rounded p-1 text-kw-muted hover:bg-kw-bg hover:text-kw-shark"
                    >
                      <Pencil className="size-3.5" />
                    </button>
                    <button
                      type="button"
                      aria-label={`Deactivate ${s.name}`}
                      onClick={() => {
                        deactivateMutation.mutate(s.id);
                      }}
                      className="rounded p-1 text-kw-muted hover:bg-kw-bg hover:text-kw-danger-text"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </section>
      ) : null}

      {seriesModal.open ? (
        <SeriesModal
          editing={seriesModal.editing}
          onClose={() => {
            setSeriesModal({ open: false, editing: null });
          }}
          onSaved={() => {
            invalidateSeries();
            toast.show(seriesModal.editing ? 'Series updated' : 'Series created', {
              variant: 'success',
            });
          }}
        />
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Series create/edit modal — schedule fields + minutes-per-segment agenda
// ─────────────────────────────────────────────────────────────────────────

function SeriesModal({
  editing,
  onClose,
  onSaved,
}: {
  editing: MeetingSeries | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(editing?.name ?? '');
  const [departmentId, setDepartmentId] = useState(editing?.departmentId ?? '');
  const [dayOfWeek, setDayOfWeek] = useState(editing?.dayOfWeek ?? 1);
  const [startTime, setStartTime] = useState(editing?.startTime ?? '09:00');
  const [durationMinutes, setDurationMinutes] = useState(editing?.durationMinutes ?? 90);
  const [segments, setSegments] = useState<AgendaSegment[]>(editing?.agenda ?? DEFAULT_L10_AGENDA);

  const departmentsQuery = useQuery({ queryKey: ['departments'], queryFn: listDepartments });

  const agendaValid = segments.every(
    (s) => Number.isInteger(s.minutes) && s.minutes >= 1 && s.minutes <= 120,
  );
  const agendaTotal = segments.reduce((sum, s) => sum + (Number.isFinite(s.minutes) ? s.minutes : 0), 0);

  const mutation = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        departmentId: departmentId || null,
        dayOfWeek,
        startTime,
        durationMinutes,
        agendaJson: segments,
      };
      return editing ? updateMeetingSeries(editing.id, body) : createMeetingSeries(body);
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  function setSegmentMinutes(index: number, minutes: number): void {
    setSegments((prev) => prev.map((s, i) => (i === index ? { ...s, minutes } : s)));
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!name.trim() || !agendaValid) return;
    mutation.mutate();
  }

  return (
    <Modal open onClose={onClose} title={editing ? 'Edit series' : 'New series'} size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Name"
          name="series-name"
          required
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
          placeholder="e.g. Leadership L10"
        />

        <label className="flex flex-col gap-1">
          <span className="text-[13px] font-medium text-kw-label">Team</span>
          <select
            value={departmentId}
            onChange={(e) => {
              setDepartmentId(e.target.value);
            }}
            className={SELECT_CLS}
          >
            <option value="">Company / leadership</option>
            {(departmentsQuery.data ?? []).map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </label>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="flex flex-col gap-1">
            <span className="text-[13px] font-medium text-kw-label">Day</span>
            <select
              value={dayOfWeek}
              onChange={(e) => {
                setDayOfWeek(Number(e.target.value));
              }}
              className={SELECT_CLS}
            >
              {DAY_LABELS.map((label, i) => (
                <option key={label} value={i}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <Input
            label="Time"
            name="series-time"
            type="time"
            required
            value={startTime}
            onChange={(e) => {
              setStartTime(e.target.value);
            }}
          />
          <Input
            label="Duration (min)"
            name="series-duration"
            type="number"
            min={15}
            max={480}
            required
            value={durationMinutes}
            onChange={(e) => {
              setDurationMinutes(Number(e.target.value));
            }}
          />
        </div>

        <fieldset className="space-y-1.5">
          <legend className="text-[13px] font-medium text-kw-label">Agenda — minutes per segment</legend>
          <div className="space-y-1.5 rounded-kw-md border border-kw-border p-3">
            {segments.map((segment, index) => (
              <label key={segment.key} className="flex items-center justify-between gap-3">
                <span className="text-[13px] text-kw-shark">{segment.label}</span>
                <input
                  type="number"
                  min={1}
                  max={120}
                  value={Number.isFinite(segment.minutes) ? segment.minutes : ''}
                  aria-label={`Minutes for ${segment.label}`}
                  onChange={(e) => {
                    setSegmentMinutes(index, Number(e.target.value));
                  }}
                  className="w-20 rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-right text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
                />
              </label>
            ))}
            <p className="pt-1 text-right text-[12px] text-kw-muted">Total: {agendaTotal} min</p>
          </div>
        </fieldset>

        {mutation.error ? (
          <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
            <p className="text-[12px] text-kw-danger-text">
              {describeEosError(mutation.error, 'Could not save the series.')}
            </p>
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={mutation.isPending} disabled={!name.trim() || !agendaValid}>
            {editing ? 'Save changes' : 'Create series'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
